export const API_ENDPOINTS = {
  auth: {
    publicKey: '/user/publickey',
    login: '/user/getUser',
    me: '/callcenter/user/me',
  },
  getUsers: '/user/getUsers',
  getRoles: '/user/getRoles',
  dropdownSet: '/patient/dropdownset',
  services: '/patient/GetServices',
  insertOrUpdateUser: '/user/insertOrUpdateUser',
  getUserCamps: '/user/getusercamps',
  // Camp dropdown source for the 9 "Other Reports" pages specifically — returns every camp
  // (id/code/name/district/etc.) in one shot when all filter ids are sent null. Do not reuse
  // this for other camp pickers in the app (All Camps, My Camps, etc.) — those already have
  // their own dedicated endpoints.
  getCampaigns: '/user/getCampaigns',
  deleteAssignedCamp: '/user/DeleteAssignedCamp',
  insertUserCampRole: '/user/insertUserCampRole',
  allLabs: '/lab/all',
  assignedUsersForLab: '/user/getAssignedUsersForLab',
  insertUserLabMapping: '/user/insertUserLabMapping',
  // "Other Reports" — the 9 operational reports (screening, referrals, lab, ad-hoc cohort
  // builder). Bare paths with no /callcenter prefix, same as getUsers/allLabs above — these are
  // separate backend controllers reachable through the same gateway.
  otherReports: {
    todaysAppointments: '/Reports/get_todays_beneficiariereport',
    campwiseAppointmentStatus: '/Reports/get_campwise_beneficiary_status_report',
    campwiseServiceStatus: '/Reports/getAppointmentServiceStatusReport',
    specialistReferrals: '/Reports/getReferralsToHigherCenterReport',
    finalReport: '/Reports/getFinalReportProcessing',
    shareDocument: '/Documentprocessing/share-document',
    referralReports: '/Reports/getReferralReport',
    labSampleReports: '/Reports/GetCampwiseLabSampleStatusReport',
    labBatches: '/lab-results/batches',
    labBatchStatus: '/lab-results/sample-report',
    advancedReportFilterOptions: '/Reports/GetReportFilterOptions',
    runAdvancedReport: '/Reports/RunAdvancedReport',
  },
  dashboard: {
    stats: '/callcenter/dashboard/stats',
    reports: '/callcenter/dashboard/reports',
  },
  batches: '/callcenter/batches',
  myBatches: '/callcenter/my-batches',
  // The id here is the batch code (e.g. BAT0001), not a numeric id — it is free text
  // stamped by the batching script, so it has to be encoded before going in the path.
  batch: (id: string): string => `/callcenter/batches/${encodeURIComponent(id)}`,
  reprocessBatchPreview: '/callcenter/batches/reprocess/preview',
  reprocessBatch: '/callcenter/batches/reprocess',
  clusterBuckets: '/callcenter/cluster-buckets',
  myClusterBuckets: '/callcenter/my-cluster-buckets',
  clusterBucketStats: '/callcenter/cluster-buckets/stats',
  myClusterBucketStats: '/callcenter/my-cluster-buckets/stats',
  clusterBucket: (id: string): string => `/callcenter/cluster-buckets/${id}`,
  clusterBucketBeneficiaries: (id: string): string => `/callcenter/cluster-buckets/${id}/beneficiaries`,
  agents: '/callcenter/agents',
  beneficiaries: '/callcenter/beneficiaries',
  beneficiary: (id: string): string => `/callcenter/beneficiaries/${id}`,
  verifyBeneficiary: (id: string): string => `/callcenter/beneficiaries/${id}/verify`,
  disposition: (id: string): string => `/callcenter/beneficiaries/${id}/disposition`,
  camps: '/callcenter/camps',
  campsMap: '/callcenter/camps/map',
  createCamp: '/callcenter/camps',
  calculateManualCampBeneficiaries: '/callcenter/camps/manual/calculate-beneficiaries',
  createManualCamp: '/callcenter/camps/manual',
  manualCampsForSupport: '/callcenter/camps/manual',
  refreshManualCampBeneficiaryMappings: (campId: number) => `/callcenter/camps/${campId}/refresh-beneficiary-mappings`,
  nextCampSequence: (districtId: number): string => `/callcenter/camps/next-sequence?districtId=${districtId}`,
  campDetail: (id: number): string => `/callcenter/camps/${id}`,
  updateCamp: (id: number): string => `/callcenter/camps/${id}/update`,
  // Code/name/date edits only. Distinct from updateCamp, which replaces a camp's whole
  // village/mandal coverage whenever villageIds or mandalIds is present — posting an empty
  // array there wipes the mapping, so an edit form must not use it.
  updateCampDetails: (id: number): string => `/callcenter/camps/${id}/details`,
  assignCamp: (campId: number): string => `/callcenter/camps/${campId}/assign`,
  aiSuggestCamps: '/callcenter/camps/ai-suggest',
  agenticPlanningGenerate: '/callcenter/agentic-planning/generate',
  agenticPlanningPrompt: '/callcenter/agentic-planning/prompt',
  agenticPlanningGenerateFromResponse: '/callcenter/agentic-planning/generate-from-response',
  agenticPlanningRefinePrompt: '/callcenter/agentic-planning/refine-prompt',
  agenticPlanningVersions: '/callcenter/agentic-planning/versions',
  agenticPlanningVersion: (id: number): string => `/callcenter/agentic-planning/versions/${id}`,
  agenticPlanningVirtualCamps: '/callcenter/agentic-planning/virtual-camps',
  agenticPlanningVirtualCampDeactivate: (id: number): string => `/callcenter/agentic-planning/virtual-camps/${id}/deactivate`,
  agenticPlanningReverseGeocode: '/callcenter/agentic-planning/reverse-geocode',
  pincodeGeocodes: '/callcenter/pincode-geocodes',
  pincodeValidation: '/callcenter/pincode-validation',
  pincodeValidationFix: '/callcenter/pincode-validation/fix',
  pincodeValidationInvalidAddresses: '/callcenter/pincode-validation/invalid-addresses',
  pincodeValidationBatchValidate: '/callcenter/pincode-validation/batch-validate',
  villagesWithoutPincode: '/callcenter/pincode-geocodes/villages-without-pincode',
  updateVillagePincodes: '/callcenter/pincode-geocodes/update-village-pincodes',
  locationGeocodingDistricts: '/callcenter/location-geocoding/districts',
  locationGeocodingMandals: '/callcenter/location-geocoding/mandals',
  locationGeocodingVillages: '/callcenter/location-geocoding/villages',
  locationGeocodingSaveDistricts: '/callcenter/location-geocoding/districts/save',
  locationGeocodingSaveMandals: '/callcenter/location-geocoding/mandals/save',
  locationGeocodingSaveVillages: '/callcenter/location-geocoding/villages/save',
  locationGeocodingPincodeMapping: '/callcenter/location-geocoding/pincode-mapping',
  locationGeocodingDistrictBoundaries: '/callcenter/location-geocoding/boundaries/district',
  locationGeocodingMandalBoundaries: '/callcenter/location-geocoding/boundaries/mandal',
  locationGeocodingVillageBoundaries: '/callcenter/location-geocoding/boundaries/village',
  locationGeocodingSaveDistrictBoundary: '/callcenter/location-geocoding/boundaries/district',
  locationGeocodingSaveMandalBoundary: '/callcenter/location-geocoding/boundaries/mandal',
  locationGeocodingSaveVillageBoundary: '/callcenter/location-geocoding/boundaries/village',
  addressGeocodesPending: '/callcenter/address-geocodes/pending',
  addressGeocodesAll: '/callcenter/address-geocodes/all',
  addressGeocodesPendingCount: '/callcenter/address-geocodes/pending-count',
  addressGeocodesSave: '/callcenter/address-geocodes/save',
  addressGeocodesGenerateDummy: '/callcenter/address-geocodes/generate-dummy',
  beneficiaryGenDistrictContext: '/callcenter/beneficiary-generation/district-context',
  beneficiaryGenCreate: '/callcenter/beneficiary-generation/create',
  createPincodeCamp: '/callcenter/pincode-camps/create',
  labsMap: '/callcenter/labs/map',
  appointments: '/patient/GetAppointmentList',
  createAppointment: '/patient/AddUpdAppointment',
  calls: '/callcenter/calls',
  clickToCall: (beneficiaryId: string): string => `/callcenter/beneficiaries/${beneficiaryId}/click-to-call`,
  clickToCallHistory: (registrationId: string): string => `/callcenter/click-to-call-history/${registrationId}`,
  liveCalls: '/callcenter/calls/live',
  hangupCall: '/callcenter/calls/hangup',
  reports: '/dashboard/reports',
  villagesMap: '/callcenter/villages/map',
  geocodeVillages: '/callcenter/villages/geocode',
  beneficiaryLocationsForPlanning: '/callcenter/villages/beneficiary-locations',
  clusterPlans: '/callcenter/clusters',
  clusterPlan: (id: string): string => `/callcenter/clusters/${id}`,
  myCamps: '/callcenter/camps/my-camps',
  campBeneficiaries: (campId: string | number): string => `/callcenter/camps/${campId}/beneficiaries`,
  allCamps: '/callcenter/camps/all',
  myDistricts: '/callcenter/my-districts',
  beneficiaryCamps: (beneficiaryId: string): string => `/callcenter/beneficiaries/${beneficiaryId}/camps`,
  managerDistricts: '/callcenter/manager-districts',
  managerDistrict: (id: number): string => `/callcenter/manager-districts/${id}`,
  agentDistricts: '/callcenter/agent-districts',
  agentDistrict: (id: number): string => `/callcenter/agent-districts/${id}`,
} as const;
